import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminMailSenderIdentities,
  fetchYowanAdminMailThreadDetail,
  getYowanApiFieldErrorMessage,
  replyYowanAdminMailThread,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const statusOptions = [
  { value: 'open', label: '対応中' },
  { value: 'pending', label: '返信待ち' },
  { value: 'closed', label: '完了' },
  { value: 'archived', label: 'アーカイブ' },
];

const statusLabels = Object.fromEntries(statusOptions.map((option) => [option.value, option.label]));

const directionLabels = {
  inbound: '受信',
  outbound: '送信',
};

const sendTaskStatusLabels = {
  queued: '送信待ち',
  pending: '送信待ち',
  submitted: '送信処理中',
  running: '送信処理中',
  success: '送信処理済み',
  sent: '送信処理済み',
  delivered: '配送済み',
  failed: '送信失敗',
  error: '送信失敗',
};

const splitAddressInput = (value = '') =>
  String(value || '')
    .split(/[\n,;]/)
    .map((item) => item.trim())
    .filter(Boolean);

const joinAddresses = (items = []) => items.filter(Boolean).join(', ') || '-';

const getSendTaskStatusLabel = (value = '') => sendTaskStatusLabels[String(value || '').toLowerCase()] || value;

const getMailApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data || {};
  const errors = responseData.errors || responseData;
  const fieldMessage = getYowanApiFieldErrorMessage({ response: { data: errors } }, [
    'sender_identity_id',
    'cc',
    'bcc',
    'body',
    'status',
    'non_field_errors',
    'detail',
  ]);

  return fieldMessage ? `${fallbackMessage} ${fieldMessage}` : fallbackMessage;
};

const isQuotedMailStartLine = (line, index) => {
  const text = String(line || '').trim();
  if (!text) {
    return false;
  }

  if (text.startsWith('>')) {
    return true;
  }

  if (/^-{2,}\s*Original Message\s*-{2,}$/i.test(text)) {
    return true;
  }

  if (/^On .+ wrote:$/i.test(text)) {
    return true;
  }

  if (/^\d{4}年.+(書きました|送信しました|記載しました)[:：]?$/.test(text)) {
    return true;
  }

  return index > 0 && /^(From|Sent|To|Subject|Date|差出人|送信日時|宛先|件名|日付)[:：]/i.test(text);
};

const splitQuotedMailBody = (body = '') => {
  const lines = String(body || '').split('\n');
  const quoteIndex = lines.findIndex((line, index) => isQuotedMailStartLine(line, index));

  if (quoteIndex <= 0) {
    return { bodyLines: lines, quotedLines: [] };
  }

  return {
    bodyLines: lines.slice(0, quoteIndex),
    quotedLines: lines.slice(quoteIndex),
  };
};

const MessageCard = ({ message, content }) => {
  const [isMetaOpen, setIsMetaOpen] = useState(false);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const { bodyLines, quotedLines } = useMemo(
    () => splitQuotedMailBody(message.textBody),
    [message.textBody]
  );
  const hasQuotedLines = quotedLines.length > 0;
  const timestamp = message.direction === 'inbound'
    ? message.receivedAt || message.createdAt
    : message.sentAt || message.createdAt;

  return (
    <article className={`yowan-admin-mail-message yowan-admin-mail-message--${message.direction || 'unknown'}`}>
      <header className="yowan-admin-mail-message__header">
        <div>
          <p className="yowan-admin-mail-message__direction">
            {directionLabels[message.direction] || message.direction || 'メール'}
          </p>
          <h2 className="yowan-admin-mail-message__subject">{message.subject || '件名なし'}</h2>
        </div>
        <p className="yowan-admin-mail-message__date">{timestamp}</p>
      </header>
      <div className="yowan-admin-mail-message__body">
        {bodyLines.map((line, index) => (
          <p key={`${message.id}-line-${index}`}>{line || '\u00a0'}</p>
        ))}
        {hasQuotedLines ? (
          <div className="yowan-admin-mail-message__quote">
            <button
              type="button"
              className="yowan-admin-mail-message__quote-toggle"
              onClick={() => setIsQuoteOpen((current) => !current)}
            >
              {isQuoteOpen
                ? content.messageQuote.closeLabel
                : `${content.messageQuote.openLabel}（${quotedLines.length}行）`}
            </button>
            {isQuoteOpen ? (
              <div className="yowan-admin-mail-message__quote-body">
                {quotedLines.map((line, index) => (
                  <p key={`${message.id}-quote-line-${index}`}>{line || '\u00a0'}</p>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="yowan-admin-mail-message__meta-toggle"
        onClick={() => setIsMetaOpen((current) => !current)}
      >
        {isMetaOpen ? content.messageMeta.closeLabel : content.messageMeta.openLabel}
      </button>
      {isMetaOpen ? (
        <dl className="yowan-admin-mail-message__meta">
          <div>
            <dt>From</dt>
            <dd>{[message.fromName, message.fromEmail].filter(Boolean).join(' / ') || '-'}</dd>
          </div>
          <div>
            <dt>To</dt>
            <dd>{joinAddresses(message.toAddresses)}</dd>
          </div>
          {message.ccAddresses.length ? (
            <div>
              <dt>Cc</dt>
              <dd>{joinAddresses(message.ccAddresses)}</dd>
            </div>
          ) : null}
          {message.deliveryStatus ? (
            <div>
              <dt>状態</dt>
              <dd>{message.deliveryStatus}</dd>
            </div>
          ) : null}
          {message.sendTaskStatus ? (
            <div>
              <dt>送信処理</dt>
              <dd>{getSendTaskStatusLabel(message.sendTaskStatus)}</dd>
            </div>
          ) : null}
          {message.effectiveReplyToEmail ? (
            <div>
              <dt>Reply-To</dt>
              <dd>{message.effectiveReplyToEmail}</dd>
            </div>
          ) : null}
          {message.actualFromEmail && message.actualFromEmail !== message.fromEmail ? (
            <div>
              <dt>実送信From</dt>
              <dd>{message.actualFromEmail}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </article>
  );
};

const ReplyForm = ({
  content,
  formRef,
  isSubmittingReply,
  message,
  onReplySubmit,
  replyValues,
  selectedIdentity,
  senderIdentities,
  updateReplyValue,
}) => (
  <form ref={formRef} className="yowan-admin-form c-surface-content yowan-admin-mail-reply-form" onSubmit={onReplySubmit}>
    <h2 className="yowan-admin-mail-section-title">{content.replyForm.title}</h2>
    <label className="c-form__field">
      <span className="c-form__label">{content.replyForm.labels.senderIdentity}</span>
      <select
        className="c-form__control"
        value={replyValues.senderIdentityId}
        disabled={isSubmittingReply}
        onChange={updateReplyValue('senderIdentityId')}
        required
      >
        <option value="">送信元を選択</option>
        {senderIdentities.map((identity) => (
          <option key={identity.id} value={identity.id}>
            {[identity.label, identity.email].filter(Boolean).join(' / ')}
          </option>
        ))}
      </select>
      <p className="yowan-admin-form__field-note yowan-admin-form__field-note--compact">
        {selectedIdentity
          ? `From: ${selectedIdentity.email}${selectedIdentity.replyToEmail ? ` / Reply-To: ${selectedIdentity.replyToEmail}` : ''}`
          : content.replyForm.senderIdentityHelpText}
      </p>
    </label>
    <label className="c-form__field">
      <span className="c-form__label">{content.replyForm.labels.cc}</span>
      <input className="c-form__control" value={replyValues.cc} onChange={updateReplyValue('cc')} disabled={isSubmittingReply} />
    </label>
    <label className="c-form__field">
      <span className="c-form__label">{content.replyForm.labels.bcc}</span>
      <input className="c-form__control" value={replyValues.bcc} onChange={updateReplyValue('bcc')} disabled={isSubmittingReply} />
    </label>
    <label className="c-form__field">
      <span className="c-form__label">{content.replyForm.labels.body}</span>
      <textarea
        className="c-form__control c-form__control--textarea"
        rows={8}
        value={replyValues.body}
        onChange={updateReplyValue('body')}
        disabled={isSubmittingReply}
        required
      />
    </label>
    <div className="yowan-admin-form__actions yowan-admin-form__actions--stacked">
      <button className="c-form__submit" type="submit" disabled={isSubmittingReply || !senderIdentities.length}>
        {isSubmittingReply ? content.replyForm.pendingMessage : content.replyForm.submitLabel}
      </button>
      {isSubmittingReply || message ? (
        <div className="yowan-admin-form__action-feedback">
          {isSubmittingReply ? <LoadingSpinner /> : null}
          {message ? <p className="yowan-admin-form__field-note">{message}</p> : null}
        </div>
      ) : null}
    </div>
  </form>
);

const AdminMailThreadDetailPage = () => {
  const content = yowanPageContent['/admin/mail/threads/:threadId'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { threadId } = useParams();
  const [thread, setThread] = useState(null);
  const [senderIdentities, setSenderIdentities] = useState([]);
  const [replyValues, setReplyValues] = useState({
    senderIdentityId: '',
    cc: '',
    bcc: '',
    body: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [message, setMessage] = useState('');
  const replyFormRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    Promise.all([
      fetchYowanAdminMailThreadDetail(threadId),
      fetchYowanAdminMailSenderIdentities().catch(() => []),
    ])
      .then(([threadPayload, identitiesPayload]) => {
        if (!isMounted) {
          return;
        }

        setThread(threadPayload);
        setSenderIdentities(identitiesPayload);
        const defaultIdentity = identitiesPayload.find((item) => item.isDefault) || identitiesPayload[0] || null;
        if (defaultIdentity) {
          setReplyValues((current) => ({
            ...current,
            senderIdentityId: current.senderIdentityId || defaultIdentity.id,
          }));
        }
      })
      .catch(() => {
        if (isMounted) {
          setThread(null);
          setLoadError(content?.loadErrorMessage || 'メールスレッドを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content?.loadErrorMessage, threadId]);

  const selectedIdentity = useMemo(
    () => senderIdentities.find((item) => item.id === replyValues.senderIdentityId) || null,
    [replyValues.senderIdentityId, senderIdentities]
  );
  const displayMessages = useMemo(
    () => (thread?.messages || []).slice().reverse(),
    [thread?.messages]
  );

  useEffect(() => {
    if (!isReplyOpen || !replyFormRef.current) {
      return;
    }

    window.setTimeout(() => {
      replyFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      replyFormRef.current?.querySelector('textarea')?.focus({ preventScroll: true });
    }, 0);
  }, [isReplyOpen]);

  if (!content) {
    return null;
  }

  const updateReplyValue = (name) => (event) => {
    setReplyValues((current) => ({
      ...current,
      [name]: event.target.value,
    }));
  };

  const refreshThread = (nextThread) => {
    setThread(nextThread);
  };

  const onReplySubmit = async (event) => {
    event.preventDefault();
    if (!thread) {
      return;
    }

    if (!replyValues.senderIdentityId) {
      setMessage('送信元を選択してください。');
      return;
    }

    if (!replyValues.body.trim()) {
      setMessage('本文を入力してください。');
      return;
    }

    setIsSubmittingReply(true);
    setMessage(content.replyForm.pendingMessage);

    try {
      const nextThread = await replyYowanAdminMailThread(thread.id, {
        senderIdentityId: replyValues.senderIdentityId,
        cc: splitAddressInput(replyValues.cc),
        bcc: splitAddressInput(replyValues.bcc),
        body: replyValues.body,
      });
      refreshThread(nextThread);
      setReplyValues((current) => ({
        ...current,
        cc: '',
        bcc: '',
        body: '',
      }));
      setIsReplyOpen(false);
      setMessage(content.replyForm.sentMessage);
    } catch (error) {
      setMessage(getMailApiErrorMessage(error, content.replyForm.errorMessage));
    } finally {
      setIsSubmittingReply(false);
    }
  };

  if (!thread) {
    return (
      <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
        <AdminPageHeader
          title={content.header.title}
          paragraphs={isLoading ? [] : [loadError || content.notFoundMessage]}
          actions={[
            <Link key="back-to-mail" to={appendYowanPreviewQueryIfNeeded('/admin/mail', preservedDomainSearch)}>
              一覧へ戻る
            </Link>,
          ]}
        />
        {isLoading ? <LoadingSpinner /> : null}
      </AdminBlogShell>
    );
  }

  const statusHref = appendYowanPreviewQueryIfNeeded(`/admin/mail/threads/${thread.id}/status`, preservedDomainSearch);

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={thread.subject || content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-mail" to={appendYowanPreviewQueryIfNeeded('/admin/mail', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
          <Link key="new-mail" to={appendYowanPreviewQueryIfNeeded('/admin/mail/new', preservedDomainSearch)}>
            新規メール
          </Link>,
          <Link key="status" to={statusHref}>
            対応状態を変更
          </Link>,
        ]}
      />

      <section className="yowan-admin-mail-summary c-surface-content">
        <dl>
          <div>
            <dt>相手</dt>
            <dd>{[thread.participantName, thread.participantEmail].filter(Boolean).join(' / ') || '-'}</dd>
          </div>
          <div>
            <dt>状態</dt>
            <dd>{thread.needsResponse ? '要返信' : statusLabels[thread.status] || thread.status || '-'}</dd>
          </div>
          <div>
            <dt>最終更新</dt>
            <dd>{thread.latestMessageAt}</dd>
          </div>
          <div>
            <dt>通数</dt>
            <dd>{thread.messageCount}通 / 未読 {thread.unreadCount}</dd>
          </div>
        </dl>
      </section>

      {message && !isReplyOpen ? (
        <div className="yowan-admin-mail-feedback c-surface-content">
          <p className="yowan-admin-form__field-note">{message}</p>
        </div>
      ) : null}

      {isReplyOpen ? (
        <ReplyForm
          content={content}
          formRef={replyFormRef}
          isSubmittingReply={isSubmittingReply}
          message={message}
          onReplySubmit={onReplySubmit}
          replyValues={replyValues}
          selectedIdentity={selectedIdentity}
          senderIdentities={senderIdentities}
          updateReplyValue={updateReplyValue}
        />
      ) : null}

      <div className="yowan-admin-mail-reply-dock" aria-label={content.replyForm.actionAreaLabel}>
        <button
          type="button"
          className="yowan-admin-mail-reply-dock__button"
          onClick={() => setIsReplyOpen((current) => !current)}
          aria-expanded={isReplyOpen}
        >
          {isReplyOpen ? content.replyForm.closeLabel : content.replyForm.openLabel}
        </button>
      </div>

      <section className="yowan-admin-mail-thread">
        {displayMessages.map((item) => (
          <MessageCard key={item.id} message={item} content={content} />
        ))}
        {!displayMessages.length ? (
          <p className="yowan-admin-form__field-note">{content.emptyMessagesText}</p>
        ) : null}
      </section>
    </AdminBlogShell>
  );
};

export default AdminMailThreadDetailPage;
